// lib/asset-blank-cover.ts
//
// A workspace version poster captured from an empty canvas is a transparent ~1KB image.
// Project cards must swap such a cover for the project's real layer image, and the only
// thing that decision needs is the asset's byte size.
//
// Reading it used to mean downloading the whole cover into a Blob from the card itself:
// the image travelled twice (once for the probe, once for the <img>), once per card.
// `/api/assets/...?meta=1` answers `{ size }` as JSON instead — zero image byte, same
// origin, no SAS redirect and therefore no CORS surface — and the probe is memoised per
// URL, so N cards showing the same cover trigger exactly one request.

/** Below this many bytes an asset cannot hold a real render: it is an empty-canvas poster. */
const BLANK_COVER_MAX_BYTES = 2048;

export const isBlankCoverSize = (size: number): boolean =>
  Number.isFinite(size) && size > 0 && size < BLANK_COVER_MAX_BYTES;

/**
 * Only our own asset route understands `?meta=1`. Any other URL (external host, direct
 * blob URL) returns null, i.e. "not probeable", and the cover is left as-is.
 */
export const buildAssetMetaUrl = (url: string): string | null => {
  const [pathAndQuery = ''] = url.split('#');
  const [path = '', query = ''] = pathAndQuery.split('?');
  if (!path.startsWith('/api/assets/')) return null;
  return query ? `${path}?${query}&meta=1` : `${path}?meta=1`;
};

const probes = new Map<string, Promise<boolean>>();

/**
 * True when the cover at `coverUrl` is a blank poster. Network or asset errors resolve to
 * false: the caller keeps the cover it already has.
 */
export const probeCoverIsBlank = (coverUrl: string): Promise<boolean> => {
  const cached = probes.get(coverUrl);
  if (cached) return cached;

  const metaUrl = buildAssetMetaUrl(coverUrl);
  const probe: Promise<boolean> = metaUrl
    ? fetch(metaUrl)
        .then(response => (response.ok ? response.json() : null))
        .then((meta: { size?: unknown } | null) =>
          typeof meta?.size === 'number' && isBlankCoverSize(meta.size)
        )
        .catch(() => false)
    : Promise.resolve(false);

  probes.set(coverUrl, probe);
  return probe;
};
